/**
 * FloatingHelpButton — bottom-right "?" button that lives on every
 * authenticated page. Opens a slide-out panel with contextual help
 * (articles whose pathHints match the current page) plus a search box
 * and a jump to the full Help Center.
 *
 * Zero LLM cost — reads static articles from /data/helpArticles.
 */
import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HelpCircle, Search, X, ChevronRight, ExternalLink, PlayCircle } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { HELP_ARTICLES, CATEGORIES, buildHintMatcher } from "@/data/helpArticles";

export default function FloatingHelpButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(null);

  const path = location.pathname || "";
  const matchHint = useMemo(() => buildHintMatcher(path), [path]);
  const results = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    const scored = HELP_ARTICLES.map((a) => {
      const contextual = matchHint(a) ? 10 : 0;
      const hay = `${a.title} ${a.keywords?.join(" ") || ""} ${a.body}`.toLowerCase();
      const matches = q ? hay.includes(q) : true;
      return { a, score: contextual, matches };
    })
      .filter((x) => x.matches)
      .sort((x, y) => y.score - x.score);
    return scored.map((x) => x.a);
  }, [query, matchHint]);

  // Hide on login/public pages — no user to help if they aren't signed
  // in. Must stay AFTER all hook calls above so React's hooks rules
  // (same count/order on every render) are preserved across the
  // visible/hidden toggle.
  const HIDDEN_PREFIXES = [
    "/login",
    "/signup",
    "/sign-document",
    "/sign-policy-document",
    "/estimate-approval",
    "/public",
    "/pay/",
    "/book/",
    "/pricing/",
    "/assessment/",
    "/share/",
  ];
  if (HIDDEN_PREFIXES.some((p) => path === p || path.startsWith(p))) return null;

  const active = HELP_ARTICLES.find((a) => a.id === activeId);
  const contextualHits = HELP_ARTICLES.filter((a) => matchHint(a));

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open Help Center"
        // Sits ABOVE the voice-assistant mic (which lives at
        // bottom-6 right-6). Stacking vertically keeps both buttons
        // in the familiar bottom-right corner without overlapping.
        className="fixed bottom-24 right-6 z-40 w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-400 shadow-lg shadow-amber-500/40 text-black flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
        data-testid="floating-help-btn"
      >
        <HelpCircle className="w-6 h-6" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="bg-zinc-950 border-zinc-800 text-white w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-white font-heading uppercase tracking-wider text-lg flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-amber-400" /> Help Center
            </SheetTitle>
            <SheetDescription className="text-zinc-400 text-xs">
              Searchable guides for every feature in CrewSync Pro.
            </SheetDescription>
          </SheetHeader>

          {/* Article detail view inside the sheet */}
          {active ? (
            <div className="mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveId(null)}
                className="text-zinc-400 hover:text-white mb-2 -ml-2"
                data-testid="floating-help-back"
              >
                <X className="w-4 h-4 mr-1" /> Back
              </Button>
              <h3 className="text-white font-heading uppercase tracking-wider text-lg mb-3">
                {active.title}
              </h3>
              <ArticleBody text={active.body} />
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search help…"
                  autoFocus
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white"
                  data-testid="floating-help-search"
                />
              </div>

              {/* Contextual block — only shown when there are hints AND
                  user hasn't typed anything yet. */}
              {!query && contextualHits.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-2">
                    For this page
                  </p>
                  <div className="space-y-2">
                    {contextualHits.map((a) => (
                      <ArticleRow key={a.id} article={a} onClick={() => setActiveId(a.id)} />
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">
                  {query ? `Search results (${results.length})` : "All guides"}
                </p>
                <div className="space-y-2">
                  {results.map((a) => (
                    <ArticleRow key={a.id} article={a} onClick={() => setActiveId(a.id)} />
                  ))}
                  {results.length === 0 && (
                    <p className="text-zinc-500 text-xs">No guides match that search.</p>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-zinc-800 flex items-center gap-2 flex-wrap">
                <Link to="/help" onClick={() => setOpen(false)}>
                  <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-200" data-testid="floating-help-open-page">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open full Help Center
                  </Button>
                </Link>
                <Link to="/help-videos" onClick={() => setOpen(false)}>
                  <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-200" data-testid="floating-help-open-videos">
                    <PlayCircle className="w-3.5 h-3.5 mr-1.5" /> Watch videos
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

const ArticleRow = ({ article, onClick }) => {
  const cat = CATEGORIES.find((c) => c.key === article.category);
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-amber-500/40 rounded-lg p-3 transition-colors group"
      data-testid={`floating-help-row-${article.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {cat && (
            <span className="text-[9px] uppercase tracking-wide text-amber-400 font-semibold">
              {cat.label}
            </span>
          )}
          <p className="text-white text-sm font-medium mt-0.5">{article.title}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-amber-400 flex-shrink-0 mt-1" />
      </div>
    </button>
  );
};

// Inline renderer (same spec as HelpCenterPage.ArticleBody). Kept in-file
// so the floating widget is self-contained — the page lazy-loads it.
const ArticleBody = ({ text }) => {
  const blocks = [];
  let bulletBuf = [];
  const inline = (s) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>');
  const flushBullets = () => {
    if (bulletBuf.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc pl-5 space-y-1 text-zinc-300 text-sm">
          {bulletBuf.map((b, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(b) }} />
          ))}
        </ul>
      );
      bulletBuf = [];
    }
  };
  (text || "").split("\n").forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushBullets();
      return;
    }
    if (line.startsWith("## ")) {
      flushBullets();
      blocks.push(
        <h4 key={`h-${idx}`} className="text-white font-heading uppercase tracking-wider text-xs mt-4">
          {line.slice(3)}
        </h4>
      );
      return;
    }
    if (line.startsWith("- ")) {
      bulletBuf.push(line.slice(2));
      return;
    }
    flushBullets();
    blocks.push(
      <p
        key={`p-${idx}`}
        className="text-zinc-300 text-sm leading-relaxed"
        dangerouslySetInnerHTML={{ __html: inline(line) }}
      />
    );
  });
  flushBullets();
  return <div className="space-y-2.5">{blocks}</div>;
};
